import React from "react";
import { Accordion } from "react-bootstrap";
import { Link } from "react-router-dom";
import { Loading } from "../../components";
import { ApiHelper, EnvironmentHelper, MessageHelper, MessageInterface, TemplateInterface, ToastHelper } from "../../helpers";

interface Props {
  shape: string
}

export const NewMessageType: React.FC<Props> = props => {

  const [templates, setTemplates] = React.useState<TemplateInterface[]>(null);
  const [canvaTemplate, setCanvaTemplate] = React.useState<TemplateInterface>(null);
  const [designerMessages, setDesignerMessages] = React.useState<MessageInterface[]>(null);
  const [yourDesignerMessages, setYourDesignerMessages] = React.useState<MessageInterface[]>(null);

  const init = () => {
    ApiHelper.get<TemplateInterface[]>("/templates/available")
      .then((data) => {
        const result: TemplateInterface[] = [];
        (data || []).forEach(d => {
          if (d.templateType === "message" && d.shapes?.indexOf(props.shape) > -1) result.push(d);
          if (d.name?.toLowerCase().includes("canva")) setCanvaTemplate(d);
        });
        setTemplates(result)
      })
      .catch(err => {
        console.error("Failed to load templates:", err);
        ToastHelper.error("Failed to load message templates");
      });

    ApiHelper.get<MessageInterface[]>("/messages/public")
      .then((data) => {
        const result: MessageInterface[] = [];
        (data || []).forEach(d => { if (d.shape === props.shape) result.push(d); });
        setDesignerMessages(result)
      })
      .catch(err => console.error("Failed to load designer messages:", err));

    ApiHelper.get<MessageInterface[]>("/messages/yourDesigner")
      .then((data) => {
        const result: MessageInterface[] = [];
        (data || []).forEach(d => { if (d.shape === props.shape) result.push(d); });
        setYourDesignerMessages(result)
      })
      .catch(err => console.error("Failed to load your designer messages:", err));
  }

  React.useEffect(init, [props.shape])

  const getAccordion = () => {
    const popularSection = getSection("popular", "Popular Message Templates", getPopular(), "fas fa-star")
    const templateSection = (templates?.length > 4) ? getSection("templates", "All Message Templates", getTemplates(), "fas fa-th") : <></>;
    const designerSection = (designerMessages?.length > 0) ? getSection("designer", "Designer Templates", getDesigner(), "fas fa-palette") : <></>;
    const yourDesignerSection = (yourDesignerMessages?.length > 0) ? getSection("yourDesigner", "Your Designer Messages", getYourDesigner(), "fas fa-user") : <></>;

    return (<Accordion id={"newMessage"} defaultActiveKey={"popular"} >
      {popularSection}
      {templateSection}
      {designerSection}
      {yourDesignerSection}
    </Accordion>)
  }

  const getSection = (eventKey: string, title: string, contents: React.ReactElement, icon: string) => {
    return (
      <Accordion.Item eventKey={eventKey}>
        <Accordion.Header><i className={icon}></i> {title}</Accordion.Header>
        <Accordion.Body>{contents}</Accordion.Body>
      </Accordion.Item>
    )
  }

  const getPopular = () => {
    const result: React.ReactElement[] = [];
    const thumbnailFile = (props.shape === "9:16") ? "thumbnailV.png" : "thumbnail.png";

    if (canvaTemplate) {
      result.push(
        <Link key={"canva"} to={"/messages/0?templateId=" + canvaTemplate.id + "&shape=" + props.shape} className="template-card">
          <div className="template-thumbnail" style={{ display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#f0f0f0" }}>
            <img src="/images/canva.png" alt="Canva" style={{ width: 64, height: 64, objectFit: "contain" }} />
          </div>
          <span className="template-name">Canva</span>
        </Link>
      );
    }

    let i = 0;
    for (let t of templates) {
      if (i < (canvaTemplate ? 4 : 5)) {
        result.push(
          <Link key={"t" + t.id} to={"/messages/0?templateId=" + t.id + "&shape=" + props.shape} className="template-card">
            <div className="template-thumbnail">
              <img src={EnvironmentHelper.ContentRoot + "/templates/" + t.id + "/" + thumbnailFile} alt={t.name} />
            </div>
            <span className="template-name">{t.name}</span>
          </Link>
        );
      }
      i++;
    }
    return (<div className="template-grid">{result}</div>);
  }

  const getTemplates = () => {
    const result: React.ReactElement[] = [];
    const thumbnailFile = (props.shape === "9:16") ? "thumbnailV.png" : "thumbnail.png";
    templates.forEach(t => {
      result.push(
        <Link key={"t" + t.id} to={"/messages/0?templateId=" + t.id + "&shape=" + props.shape} className="template-card">
          <div className="template-thumbnail">
            <img src={EnvironmentHelper.ContentRoot + "/templates/" + t.id + "/" + thumbnailFile} alt={t.name} />
          </div>
          <span className="template-name">{t.name}</span>
        </Link>
      );
    });
    return (<div className="template-grid">{result}</div>);
  }

  const getDesigner = () => {
    const result: React.ReactElement[] = [];
    designerMessages?.forEach(m => {
      const thumbPath = MessageHelper.getThumbnailPath(m);
      result.push(
        <Link key={"d" + m.id} to={"/messages/0?templateId=" + m.templateId + "&shape=" + props.shape + "&copyFrom=" + m.id} className="template-card">
          <div className="template-thumbnail">
            <img src={thumbPath} alt={m.name} />
          </div>
          <span className="template-name">{m.name}</span>
        </Link>
      );
    });
    return (<div className="template-grid">{result}</div>);
  }

  const getYourDesigner = () => {
    const result: React.ReactElement[] = [];
    yourDesignerMessages?.forEach(m => {
      const thumbPath = MessageHelper.getThumbnailPath(m);
      result.push(
        <Link key={"yd" + m.id} to={"/messages/0?templateId=" + m.templateId + "&shape=" + props.shape + "&copyFrom=" + m.id} className="template-card">
          <div className="template-thumbnail">
            <img src={thumbPath} alt={m.name} />
          </div>
          <span className="template-name">{m.name}</span>
        </Link>
      );
    });
    return (<div className="template-grid">{result}</div>);
  }

  if (templates === null) return <Loading />
  else return getAccordion();
};